import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Helper for Authorization header
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
};

// Helper to ensure error is always a string
const formatError = (error) => {
  if (typeof error === 'string') return error;
  if (error?.message) return error.message;
  if (error?.response?.data?.message) return error.response.data.message;
  if (typeof error?.response?.data === 'string') return error.response.data;
  return 'An unexpected error occurred';
};

// Helper to clean payload (remove undefined/null)
const cleanPayload = (obj) => {
  const cleaned = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Normalize loanType value before sending to backend
const normalizeLoanType = (loanType) => {
  if (!loanType) return loanType;
  const lt = loanType.toLowerCase();
  if (lt.includes('fixed')) return 'Fixed';
  if (lt.includes('personal')) return 'Personal';
  return loanType.charAt(0).toUpperCase() + loanType.slice(1).toLowerCase();
};

// Fetch Loan Services with superAdminId
export const fetchServices = createAsyncThunk(
  'applyLoan/fetchServices',
  async (superAdminId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/get-loan`, {
        ...getAuthHeaders(),
        params: { superAdminId }, // Pass superAdminId as query param
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(formatError(error));
    }
  }
);


// Submit Loan Application
export const submitLoanApplication = createAsyncThunk(
  'applyLoan/submitLoanApplication',
  async (loanPayload, { rejectWithValue }) => {
    try {
      // Normalize loanType just in case
      const normalizedPayload = {
        ...loanPayload,
        loanType: normalizeLoanType(loanPayload.loanType),
      };
      // Clean the payload of null/undefined/empty strings
      const cleanedPayload = cleanPayload(normalizedPayload);

      const response = await axios.post(
        `${API_URL}/new-apply-loan-aayog`,
        cleanedPayload,
        getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(formatError(error));
    }
  }
);



export const fetchCustomerByUserId = createAsyncThunk(
  "applyLoan/fetchCustomerByUserId",
  async (userId, { rejectWithValue }) => {
    const branchId = localStorage.getItem("userId") || "18"; // ✅ Always include branchId

    try {
      const response = await axios.get(
        `https://payyug.in/api/customers-get-userId`,
        {
          ...getAuthHeaders(),
          params: { userId, branchId }, // ✅ Send branchId along with userId
        }
      );

      console.log("API Response:", response.data);

      return response.data.data; // Return customer object
    } catch (error) {
      return rejectWithValue(formatError(error));
    }
  }
);





const applyLoanSlice = createSlice({
  name: 'applyLoan',
  initialState: {
    services: [],
    selectedService: null,
    customer: null,   // For storing customer data
    customerLoading: false,
    loanResponse: null, // ✅ add this


    loading: false,
    error: null,
    submitStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  },
  reducers: {
    setSelectedService(state, action) {
      state.selectedService = action.payload
        ? state.services.find(s => s.serviceName === action.payload) || null
        : null;
    },
    clearSubmitStatus(state) {
      state.submitStatus = 'idle';
      state.error = null;
      state.loanResponse = null; // ✅ clear response when resubmitting

    },

    clearCustomer: (state) => {
      state.customer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload || [];
        state.error = null;
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load loan services';
      })
      .addCase(submitLoanApplication.pending, (state) => {
        state.submitStatus = 'loading';
        state.error = null;
      })
      .addCase(submitLoanApplication.fulfilled, (state, action) => {
        state.submitStatus = 'succeeded';
        state.error = null;
        state.loanResponse = action.payload; // ✅ store response from API

      })
      .addCase(submitLoanApplication.rejected, (state, action) => {
        state.submitStatus = 'failed';
        state.error = action.payload || 'Failed to submit loan application';
      })


      // for customer
         .addCase(fetchCustomerByUserId.pending, (state) => {
      state.customerLoading = true;
      state.error = null;
    })
    .addCase(fetchCustomerByUserId.fulfilled, (state, action) => {
      state.customerLoading = false;
      state.customer = action.payload || null;
    })
    .addCase(fetchCustomerByUserId.rejected, (state, action) => {
      state.customerLoading = false;
      state.error = action.payload || 'Failed to fetch customer data';
    });
  },
});

export const { setSelectedService, clearSubmitStatus , clearCustomer} = applyLoanSlice.actions;
export default applyLoanSlice.reducer;


// api integrate get api https://payyug.in/api/customers-get-userId one customer data